import type { WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';

/**
 * Shared Wazuh API scope helpers for SOC dashboards and EDR list pages.
 * Extracted from duplicated logic in useWazuhDashboardData / useWazuhOverviewData.
 *
 * Force `resellerId` to the logged-in user's profile organization id, and drop
 * `organizationId` when it merely mirrors that profile id. This preserves the
 * client-filter contract: `organizationId` may only carry a value picked by the
 * reseller from the client dropdown (which differs from the profile org id).
 */
export const applyProfileResellerScope = (
  scope: WazuhScopeParams,
  profileOrgId: string | undefined
): WazuhScopeParams => {
  if (!profileOrgId) return scope;
  const { organizationId, ...rest } = scope;
  const next: WazuhScopeParams = { ...rest, resellerId: profileOrgId };
  if (organizationId && organizationId !== profileOrgId) {
    next.organizationId = organizationId;
  }
  return next;
};

type BuildEdrApiScopeArgs = {
  scope: WazuhScopeParams;
  profileOrgId: string | undefined;
  isReseller: boolean;
  /** Page-local Organization filter; independent of SOC All Clients. */
  pageOrganizationId?: string;
};

/**
 * Builds query scope for EDR list/summary APIs.
 * Ignores global `scope.organizationId` (SOC All Clients).
 * Reseller: resellerId = profile org; optional organizationId from page filter.
 * Sub-org / client: organizationId = profile org only.
 */
export const buildEdrApiScope = ({
  scope,
  profileOrgId,
  isReseller,
  pageOrganizationId,
}: BuildEdrApiScopeArgs): WazuhScopeParams => {
  const datesAndListMeta: WazuhScopeParams = {
    startDate: scope.startDate,
    endDate: scope.endDate,
    search: scope.search,
    sort: scope.sort,
    column: scope.column,
  };

  if (isReseller && profileOrgId) {
    return {
      ...datesAndListMeta,
      resellerId: profileOrgId,
      ...(pageOrganizationId && pageOrganizationId !== profileOrgId
        ? { organizationId: pageOrganizationId }
        : {}),
    };
  }

  if (profileOrgId) {
    return { ...datesAndListMeta, organizationId: profileOrgId };
  }

  return datesAndListMeta;
};

type BuildEndpointsApiScopeArgs = {
  scope: WazuhScopeParams;
  profileOrgId: string | undefined;
  isReseller: boolean;
  /** Page-local Organization filter (reseller only). */
  pageOrganizationId?: string;
};

/**
 * Scope for GET /api/v1/endpoints.
 * Reseller: resellerId + optional organizationId from org filter.
 * Client org: organizationId only (profile org id).
 */
export const buildEndpointsApiScope = ({
  scope,
  profileOrgId,
  isReseller,
  pageOrganizationId,
}: BuildEndpointsApiScopeArgs): WazuhScopeParams => {
  const dates = {
    startDate: scope.startDate,
    endDate: scope.endDate,
  };

  if (isReseller && profileOrgId) {
    return {
      ...dates,
      resellerId: profileOrgId,
      ...(pageOrganizationId ? { organizationId: pageOrganizationId } : {}),
    };
  }

  if (profileOrgId) {
    return { ...dates, organizationId: profileOrgId };
  }

  return dates;
};
